import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt, fmtDateShort } from '../lib/format';
import { Trophy, CheckCircle } from '@phosphor-icons/react';

export default function MySalary() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/salary-payments/me').then(r => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const totalReceived = items.reduce((s, i) => s + (i.net_paid || 0), 0);
  const lastPayment = items[0];

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="my-salary-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">我的工资</h1>
        <p className="text-sm text-slate-400 mt-0.5">{user?.name} · 月薪 RM {fmt(user?.monthly_salary || 0)}</p>
      </div>

      {/* Hero card */}
      {lastPayment && (
        <div className="card-base p-5 sm:p-6 border-emerald-500/30" style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(23,33,54,1))'
        }} data-testid="last-payment-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Trophy size={22} weight="duotone" className="text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-sm text-emerald-300 font-semibold">{lastPayment.month} 工资 · 已支付</div>
              <div className="text-xs text-slate-400 mt-0.5">
                由 {lastPayment.paid_by} 于 {fmtDateShort(lastPayment.paid_at)} 发放
              </div>
            </div>
            <CheckCircle size={24} weight="fill" className="text-emerald-400" />
          </div>
          <div className="space-y-2 bg-slate-900/40 rounded-lg p-4">
            <Line label="底薪" value={`RM ${fmt(lastPayment.base_salary)}`} />
            {lastPayment.advance_deducted > 0 && (
              <Line label={`借支抵扣 (${lastPayment.advance_count} 笔)`} value={`- RM ${fmt(lastPayment.advance_deducted)}`} tone="amber" />
            )}
            {lastPayment.other_deductions > 0 && (
              <Line label={`杂费扣款${lastPayment.other_deductions_notes ? ' · ' + lastPayment.other_deductions_notes : ''}`} value={`- RM ${fmt(lastPayment.other_deductions)}`} tone="amber" />
            )}
            <div className="h-px bg-slate-700/50 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-base font-semibold text-white">实际收到</span>
              <span className="font-num text-2xl font-bold text-emerald-400" data-testid="last-net-paid">RM {fmt(lastPayment.net_paid)}</span>
            </div>
            {lastPayment.notes && (
              <div className="text-xs text-slate-500 pt-1">备注:{lastPayment.notes}</div>
            )}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card-base p-4">
          <div className="label-tiny mb-1">累计已收</div>
          <div className="font-num text-2xl font-semibold text-emerald-300">RM {fmt(totalReceived)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{items.length} 个月</div>
        </div>
        <div className="card-base p-4">
          <div className="label-tiny mb-1">当前月薪</div>
          <div className="font-num text-2xl font-semibold text-white">RM {fmt(user?.monthly_salary || 0)}</div>
          <div className="text-xs text-slate-500 mt-0.5">基本月薪</div>
        </div>
      </div>

      {/* History */}
      <div className="card-base p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-3">发放历史</h2>
        {loading && <div className="text-sm text-slate-500 py-4 text-center">加载中…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm text-slate-500 py-8 text-center">还没有工资发放记录</div>
        )}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map(p => (
              <div key={p.id} className="p-3 rounded-lg bg-slate-900/40 border border-slate-800" data-testid={`my-salary-${p.month}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm font-semibold text-white">{p.month}</div>
                    <div className="text-xs text-slate-500 font-num mt-1">
                      底薪 RM {fmt(p.base_salary)}
                      {p.advance_deducted > 0 && <> · 借支 -RM {fmt(p.advance_deducted)}</>}
                      {p.other_deductions > 0 && <> · 杂费 -RM {fmt(p.other_deductions)}</>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {fmtDateShort(p.paid_at)} · {p.paid_by}
                    </div>
                  </div>
                  <div className="font-num text-lg font-semibold text-emerald-400 whitespace-nowrap">
                    RM {fmt(p.net_paid)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Line({ label, value, tone }) {
  const cls = tone === 'amber' ? 'text-amber-300' : 'text-white';
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-slate-400">{label}</span>
      <span className={`font-num ${cls}`}>{value}</span>
    </div>
  );
}
